import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Which sections are open, and what order they sit in.
 *
 * The sidebar is 39 items across 6 sections, all expanded permanently — reaching
 * Subscriptions meant scrolling past thirty links. Sections now collapse, and because the
 * useful order depends on the person (a coach lives in 1-1 training; an admin lives in
 * Administration) they can be dragged.
 *
 * Stored per BROWSER rather than per account, deliberately. Account-level storage needs a
 * preferences field and an endpoint; this ships with no backend change and no risk to
 * anyone else's sidebar. If it turns out people really do reorder, moving the same two
 * keys server-side later is contained — and the shape below is what would be sent.
 *
 * Unknown ids in a saved order are dropped and new sections are appended, so adding a
 * section in code never leaves someone with a sidebar missing it.
 */
const ORDER_KEY = 'cf.sidebar.order.v1';
const OPEN_KEY = 'cf.sidebar.open.v1';
/**
 * Order of the LINKS inside a section, as { [groupId]: [itemId, ...] }.
 *
 * A separate key from the section order rather than a nested structure: a
 * corrupt or stale value for one must not take the other down, and someone who
 * has reordered sections should not lose that when item ordering changes shape.
 */
const ITEM_ORDER_KEY = 'cf.sidebar.itemorder.v1';

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private mode, disabled storage, or corrupt JSON — fall back to defaults rather than
    // taking the whole sidebar down with it.
    return null;
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* not worth surfacing: the sidebar still works, it just won't be remembered */
  }
};

export const useSidebarLayout = (groupIds, activeGroupId) => {
  const [order, setOrder] = useState(() => read(ORDER_KEY) || []);
  const [open, setOpen] = useState(() => read(OPEN_KEY) || {});
  const [itemOrder, setItemOrder] = useState(() => read(ITEM_ORDER_KEY) || {});

  // Reconcile the saved order against the sections that actually exist right now.
  const orderedIds = useMemo(() => {
    const known = new Set(groupIds);
    const kept = order.filter((id) => known.has(id));
    const added = groupIds.filter((id) => !kept.includes(id));
    return [...kept, ...added];
  }, [order, groupIds]);

  // The section holding the current page is always open. Without this, navigating from a
  // link elsewhere in the panel lands you in a collapsed sidebar with no indication of
  // where you are.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpen((prev) => (prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true }));
  }, [activeGroupId]);

  useEffect(() => {
    write(OPEN_KEY, open);
  }, [open]);

  const isOpen = useCallback(
    (id) => {
      if (id in open) return open[id];
      // First visit: show the section you are in, fold the rest. Opening everything would
      // reproduce the problem this exists to solve.
      return id === activeGroupId;
    },
    [open, activeGroupId]
  );

  const toggle = useCallback((id) => {
    setOpen((prev) => ({ ...prev, [id]: !(id in prev ? prev[id] : false) }));
  }, []);

  const move = useCallback(
    (fromId, toId) => {
      if (!fromId || !toId || fromId === toId) return;
      const next = orderedIds.slice();
      const from = next.indexOf(fromId);
      const to = next.indexOf(toId);
      if (from < 0 || to < 0) return;
      next.splice(to, 0, next.splice(from, 1)[0]);
      setOrder(next);
      write(ORDER_KEY, next);
    },
    [orderedIds]
  );

  /**
   * Order the links inside one section.
   *
   * Same reconciliation rule as sections: unknown ids in a saved order are
   * dropped and new ones are appended. So adding a page in code never leaves
   * somebody with a sidebar missing it, and removing one never leaves a gap —
   * which matters more here, because permissions already vary the child list
   * per user and a saved order will routinely contain ids that person cannot
   * see.
   */
  const orderItems = useCallback(
    (groupId, children) => {
      const saved = itemOrder[groupId];
      if (!saved?.length) return children;
      const byId = new Map(children.map((c) => [c.id, c]));
      const kept = saved.map((id) => byId.get(id)).filter(Boolean);
      const keptIds = new Set(kept.map((c) => c.id));
      return [...kept, ...children.filter((c) => !keptIds.has(c.id))];
    },
    [itemOrder]
  );

  const moveItem = useCallback(
    (groupId, fromId, toId, children) => {
      if (!groupId || !fromId || !toId || fromId === toId) return;
      const current = orderItems(groupId, children).map((c) => c.id);
      const from = current.indexOf(fromId);
      const to = current.indexOf(toId);
      if (from < 0 || to < 0) return;
      current.splice(to, 0, current.splice(from, 1)[0]);
      const next = { ...itemOrder, [groupId]: current };
      setItemOrder(next);
      write(ITEM_ORDER_KEY, next);
    },
    [itemOrder, orderItems]
  );

  const reset = useCallback(() => {
    setOrder([]);
    setOpen({});
    setItemOrder({});
    write(ORDER_KEY, []);
    write(OPEN_KEY, {});
    write(ITEM_ORDER_KEY, {});
  }, []);

  const isCustomised = order.length > 0 || Object.keys(itemOrder).length > 0;

  return { orderedIds, isOpen, toggle, move, orderItems, moveItem, reset, isCustomised };
};

export default useSidebarLayout;
